import { useRef, useState } from "react";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Loader2, RotateCcw, Save } from "lucide-react";
import { PLACEHOLDERS, toFriendly } from "@/lib/emailTemplates";

export const TemplateEditor = ({ asset, script, saved, onSaved, onCancel }) => {
  const [subject, setSubject] = useState(toFriendly(saved?.subject ?? asset.email_subject ?? ""));
  const [body, setBody] = useState(toFriendly(saved?.body ?? asset.email_body ?? ""));
  const [busy, setBusy] = useState(false);
  const ref = useRef(null);

  const insert = (tok) => {
    const el = ref.current;
    const s = el?.selectionStart ?? body.length, e = el?.selectionEnd ?? body.length;
    setBody(body.slice(0, s) + tok + body.slice(e));
    setTimeout(() => { el?.focus(); el?.setSelectionRange(s + tok.length, s + tok.length); }, 0);
  };
  const save = async () => {
    if (!body.trim()) { toast.error("The template can't be empty"); return; }
    setBusy(true);
    try { await api.put(`/marketing/templates/${asset.id}`, { subject, body }); toast.success("Your template is saved"); onSaved({ subject, body }); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const restore = async () => {
    if (!window.confirm("Go back to the Velopaths version of this template? Your edits will be removed.")) return;
    setBusy(true);
    try { await api.delete(`/marketing/templates/${asset.id}`); toast.success("Restored the Velopaths version"); onSaved(null); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-3 rounded-xl border border-primary/40 p-4" data-testid="template-editor">
      <p className="text-sm text-muted-foreground">Edit your own version of this template. Words in [brackets] fill in automatically each time you use it.</p>
      <div className="flex flex-wrap gap-1.5" data-testid="template-placeholders">
        {PLACEHOLDERS.map((p) => (
          <button key={p} type="button" onClick={() => insert(p)} className="text-xs rounded-full border border-border px-2.5 py-1 hover:border-primary hover:text-primary transition-colors" data-testid={`template-insert-${p.replace(/[[\]]/g, "").replace(/\s+/g, "-").toLowerCase()}`}>{p}</button>
        ))}
      </div>
      {!script && <div className="space-y-1.5"><Label>Subject</Label><Input value={subject} onChange={(e) => setSubject(e.target.value)} data-testid="template-edit-subject" /></div>}
      <div className="space-y-1.5"><Label>{script ? "Script" : "Email"}</Label><Textarea ref={ref} rows={16} value={body} onChange={(e) => setBody(e.target.value)} className="text-sm leading-relaxed" data-testid="template-edit-body" /></div>
      <div className="flex flex-col-reverse sm:flex-row gap-2 sm:justify-between">
        {saved ? <Button variant="ghost" onClick={restore} disabled={busy} data-testid="template-restore-btn"><RotateCcw className="w-4 h-4 mr-2" />Restore Velopaths version</Button> : <span />}
        <div className="flex gap-2 justify-end">
          <Button variant="ghost" onClick={onCancel} disabled={busy} data-testid="template-cancel-btn">Cancel</Button>
          <Button onClick={save} disabled={busy} data-testid="template-save-btn">{busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}Save my template</Button>
        </div>
      </div>
    </div>
  );
};
